'use client'

import { useEffect, useState } from 'react'
import useActionPointsQueryV1 from '@/api/admin/actionPoints/useActionPointsQueryV1'
import ActionPointForm from '@/components/backoffice/forms/ActionPointForm'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { useDialog } from '@/components/ui/useDialog'
import { useUserStore } from '@/stores/user-store'
import { ActionPoint, SurveyGroup } from '@/types'
import { actionPointIsCompletelyFilled } from '@/utils/actionPoints'
import { confetti3 } from '@/utils/confetti'
import { cn } from '@/utils/tailwind/utils'

const LOCAL_STORAGE_KEY = '__ap_checker__'
const USER_EMAILS = ['leonie@nriched.org']

interface Props {
  className?: string
}

const ActionPointChecker = ({ className }: Props) => {
  const user = useUserStore((state) => state.user)
  const isAdmin = useUserStore((state) => state.isAdmin)
  const { open: openDialog } = useDialog()
  const [todayIsDone, setTodayIsDone] = useState(true)
  const [open, setOpen] = useState(false)
  const [actionPoint, setActionPoint] = useState<ActionPoint | undefined>(undefined)

  const { data: actionPoints } = useActionPointsQueryV1(!!isAdmin && !todayIsDone)

  const isFirstTimeToday = () => {
    const lastChecked = localStorage.getItem(LOCAL_STORAGE_KEY)
    const today = new Date().toDateString()
    if (lastChecked !== today) {
      return true
    }
    return false
  }

  useEffect(() => {
    if (isAdmin && user && USER_EMAILS.includes(user.email || '')) {
      setTodayIsDone(!isFirstTimeToday())
    }
  }, [isAdmin, user])

  useEffect(() => {
    if (isAdmin && actionPoints) {
      // only employees
      const employeesAPs = actionPoints.filter((ap) => ap.surveyGroupType === SurveyGroup.EMPLOYEE)
      // the next not completely filled action point
      const nextActionPoint = employeesAPs.find((ap) => !actionPointIsCompletelyFilled(ap))
      if (nextActionPoint) {
        setActionPoint(nextActionPoint)
        setOpen(true)
      }
    }
  }, [isAdmin, actionPoints])

  const preventEvents = (event?: React.MouseEvent<HTMLDivElement>) => {
    event?.preventDefault()
    event?.stopPropagation()
  }

  if (!isAdmin) {
    return null
  }

  const onFormSaved = () => {
    finishForToday()
    confetti3()

    openDialog({
      contentClass: '!w-[450px]',
      title: 'Toll gemacht !!!',
      data: 'Morgen kriegst du dann deinen neuen Aktionspunkt ;-)',
      nextBtnText: 'Ich will noch einen Aktionspunkt ausfüllen',
      onClose: (confirmResult?: boolean) => {
        if (confirmResult) {
          localStorage.setItem(LOCAL_STORAGE_KEY, 'xxxx')
          window.location.reload()
        }
      },
    })
  }

  const finishForToday = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, new Date().toDateString())
    setTodayIsDone(true)
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        aria-description={actionPoint?.id}
        aria-describedby={'action-point-checker-description'}
        // onContentClick={preventEvents}
        onOverlayClick={preventEvents}
        className="h-full max-h-[95vh] bg-pink-100 p-6">
        <div className={cn('flex h-full max-h-full flex-col gap-4 overflow-hidden', className)}>
          <DialogTitle className="flex w-full items-center justify-between">
            Liebe Leonie, füll doch gleich mal diesen Aktionspunkt aus:{' '}
            <button
              onClick={finishForToday}
              className="mr-2 rounded border border-slate-600 px-2 py-1 text-sm font-normal hover:bg-slate-100">
              heute nicht mehr anzeigen
            </button>
          </DialogTitle>
          <div className="flex-1 overflow-auto bg-violet-100">
            <ActionPointForm
              actionPoint={actionPoint}
              onUpdate={onFormSaved}
              onClose={() => setOpen(false)}
              openTechnicalDetails={true}
              className="rounded p-2"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ActionPointChecker
